/**
 * Emergency Plan & Procedure Tab Component
 * Restructured to match standard childcare emergency plan document sections
 */
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { Shield, Printer, Save, History, Download, Loader2, ChevronDown, ChevronUp, Upload, FileText, X, Plus, Trash2 } from "lucide-react";
import { format } from "date-fns";
import { useAuth } from "@/contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Defined OUTSIDE the component so they are never recreated on re-render
const SectionHeader = ({ title, section, expanded, onToggle }) => (
  <button onClick={() => onToggle(section)}
    className="w-full flex items-center justify-between p-3 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-left">
    <span className="font-semibold text-sm">{title}</span>
    {expanded ? <ChevronUp className="w-4 h-4 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 flex-shrink-0" />}
  </button>
);

const Field = ({ label, value, onChange, placeholder, type = "text" }) => (
  <div className="space-y-1">
    <Label className="text-xs">{label}</Label>
    <Input
      type={type}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="h-8 text-sm"
    />
  </div>
);

export const EmergencyPlanProcedureTab = () => {
  const { organization } = useAuth();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const [savedPlans, setSavedPlans] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const [expandedSections, setExpandedSections] = useState({
    general: true,
    evacuation: false,
    lockdown: false,
    fire: false,
    carbon: false,
    weather: false,
    illness: false,
    communicable: false,
    utilities: false,
    lossOfChild: false,
    injury: false,
    bombThreat: false,
    chemical: false,
    dangerousPerson: false,
    specialNeeds: false,
    contacts: false,
  });

  const [formData, setFormData] = useState({
    // Facility Info
    facilityName: "",
    facilityAddress: "",
    facilityDirector: "",
    facilityPhone: "",
    licenseNumber: "",
    lastReviewDate: format(new Date(), 'yyyy-MM-dd'),

    // I. Evacuation
    primaryAssemblyArea: "",
    secondaryAssemblyArea: "",
    relocationSite: "",
    relocationSiteAddress: "",

    // II. Lockdown / Shelter-in-Place
    shelterInPlaceAtFacility: "",
    shelterInPlaceOffSite: "",
    emergencyRadioLocation: "",

    // III. Fire Safety
    fireExtinguisherLocations: ["", "", "", ""],
    fireAssemblyArea: "",
    fireRelocationSite: "",

    // V. Inclement Weather
    severeWeatherShelterFacility: "",
    severeWeatherShelterOffSite: "",
    tornadoSafePlace: "",

    // VI. Illness / Injury
    firstAidPersonnel: [{ name: "", location: "" }],
    firstAidKitLocations: "",
    hospitalName: "",
    hospitalAddress: "",
    hospitalPhone: "",

    // VII. Utilities
    flashlightLocation: "",
    batteriesLocation: "",
    radioLocation: "",
    waterSupplyLocation: "",
    cellPhoneLocation: "",
    waterCompanyPhone: "",
    powerCompanyPhone: "",
    publicWorksPhone: "",
    telephoneRepairPhone: "",
    hvacContactInfo: "",
    plumbingContactInfo: "",
    locksmithContactInfo: "",
    heatClosureTemp: "65",
    coolingClosureTemp: "85",

    // Infants / Special Needs
    caregiverGoBagLocation: "",
    infantEvacuationNotes: "",

    // Emergency Contacts
    emergencyContacts: [
      { role: "Fire Department", phone: "911" },
      { role: "Police Department", phone: "911" },
      { role: "Emergency Medical Services", phone: "911" },
      { role: "Poison Control", phone: "" },
      { role: "Local Hospital", phone: "" },
      { role: "Water Company", phone: "" },
      { role: "Power Company", phone: "" },
      { role: "OCDEL / DECAL (State)", phone: "" },
    ],

    // Approval
    approvedBy: "",
    approvalDate: format(new Date(), 'yyyy-MM-dd'),
  });

  useEffect(() => { fetchPlans(); }, []);

  useEffect(() => {
    if (organization?.name) {
      setFormData(prev => ({
        ...prev,
        facilityName: prev.facilityName || organization.name,
        facilityAddress: prev.facilityAddress || organization.address || "",
        licenseNumber: prev.licenseNumber || organization.licenseNumber || "",
      }));
    }
  }, [organization]);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/compliance/emergency-plan-procedure`);
      setSavedPlans(res.data?.plans || []);
      const latestForm = (res.data?.plans || []).find(p => p.recordType !== 'file_upload');
      if (latestForm) {
        const { id, recordType, fileName, fileType, fileSize, createdAt, updatedAt, createdBy, organizationId, ...formFields } = latestForm;
        setFormData(prev => ({ ...prev, ...formFields }));
      }
    } catch (error) {
      console.error("Error fetching plans:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));
  const handleArrayChange = (field, index, value) => setFormData(prev => {
    const arr = [...(prev[field] || [])]; arr[index] = value; return { ...prev, [field]: arr };
  });
  const handleObjectArrayChange = (field, index, key, value) => setFormData(prev => {
    const arr = [...(prev[field] || [])]; arr[index] = { ...arr[index], [key]: value }; return { ...prev, [field]: arr };
  });
  const addToArray = (field, defaultItem) => setFormData(prev => ({ ...prev, [field]: [...(prev[field] || []), defaultItem] }));
  const removeFromArray = (field, index) => setFormData(prev => ({ ...prev, [field]: (prev[field] || []).filter((_, i) => i !== index) }));
  const toggleSection = (section) => setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));

  // S and F are regular functions (not components) — safe to define inside the component
  // They call the STABLE outer SectionHeader and Field components
  const S = (title, section) => (
    <SectionHeader title={title} section={section} expanded={expandedSections[section]} onToggle={toggleSection} />
  );
  const F = (label, field, placeholder, type = "text") => (
    <Field label={label} value={formData[field] || ""} onChange={v => handleInputChange(field, v)} placeholder={placeholder} type={type} />
  );

  const handleSave = async () => {
    try {
      setSaving(true);
      const { id, recordType, fileName, fileType, fileSize, createdAt, updatedAt, createdBy, organizationId, ...clean } = formData;
      await axios.post(`${API}/compliance/emergency-plan-procedure`, clean);
      toast.success("Emergency Plan & Procedure saved");
      fetchPlans();
    } catch { toast.error("Failed to save plan"); } finally { setSaving(false); }
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    const allowed = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpeg', 'image/png'];
    if (!allowed.includes(file.type)) { toast.error("Upload PDF, DOC, DOCX, or image file"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("File must be under 10MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      await axios.post(`${API}/compliance/emergency-plan-procedure/upload-file`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success("Emergency plan uploaded and saved to history");
      fetchPlans();
    } catch { toast.error("Failed to upload document"); } finally { setUploading(false); }
  };

  const handleDownloadPlan = async (plan) => {
    const token = localStorage.getItem('token');
    const a = document.createElement('a');
    a.href = `${API}/compliance/emergency-plan-procedure/${plan.id}/download?token=${encodeURIComponent(token)}`;
    a.download = plan.fileName || 'emergency-plan.pdf';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  const handleDeletePlan = async (planId) => {
    try {
      await axios.delete(`${API}/compliance/emergency-plan-procedure/${planId}`);
      setSavedPlans(prev => prev.filter(p => p.id !== planId));
      toast.success("Record deleted");
    } catch { toast.error("Failed to delete"); }
  };

  const handlePrintPlan = async (plan) => {
    if (plan.recordType === 'file_upload') {
      const token = localStorage.getItem('token');
      window.open(`${API}/compliance/emergency-plan-procedure/${plan.id}/view?token=${token}`, '_blank');
      return;
    }
    handlePrint(plan);
  };

  const handleDrop = (e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) handleFileUpload(f); };

  const handlePrint = (data = formData) => {
    const w = window.open('', '_blank');
    if (!w) { toast.error("Allow popups to print"); return; }
    w.document.write(`<!DOCTYPE html><html><head><title>Emergency Plan - ${data.facilityName||''}</title>
    <style>body{font-family:Arial,sans-serif;padding:30px;max-width:800px;margin:0 auto;font-size:11px;}
    h1{text-align:center;font-size:16px;}h2{font-size:13px;margin-top:18px;background:#f3f4f6;padding:6px 8px;border-left:4px solid #374151;}
    h3{font-size:11px;margin-top:12px;font-weight:bold;}
    .field{margin-bottom:6px;}.fl{font-weight:bold;}
    table{width:100%;border-collapse:collapse;margin-top:8px;font-size:10px;}
    th,td{border:1px solid #ccc;padding:5px;text-align:left;}th{background:#f3f4f6;}
    ul{margin:4px 0;padding-left:18px;}li{margin-bottom:2px;}
    .header{text-align:center;border:2px solid #000;padding:10px;margin-bottom:16px;}
    @media print{body{padding:10px;}}</style></head><body>
    <div class="header">
      <h1>CHILD CARE FACILITY EMERGENCY PLAN & PROCEDURE</h1>
      <p><strong>${data.facilityName||'[Facility Name]'}</strong> | ${data.facilityAddress||'[Address]'}</p>
      <p>Director: ${data.facilityDirector||'___'} | Phone: ${data.facilityPhone||'___'} | License #: ${data.licenseNumber||'___'}</p>
      <p>Last Review: ${data.lastReviewDate||'___'} | Approved By: ${data.approvedBy||'___'} | Date: ${data.approvalDate||'___'}</p>
    </div>
    <h2>I. EVACUATION</h2>
    <div class="field"><span class="fl">Primary Assembly Area:</span> ${data.primaryAssemblyArea||'___'}</div>
    <div class="field"><span class="fl">Secondary Assembly Area (if primary blocked):</span> ${data.secondaryAssemblyArea||'___'}</div>
    <div class="field"><span class="fl">Relocation Site (if facility cannot be reentered):</span> ${data.relocationSite||'___'} ${data.relocationSiteAddress ? `— ${data.relocationSiteAddress}` : ''}</div>
    <h2>II. LOCKDOWN / SHELTER-IN-PLACE</h2>
    <div class="field"><span class="fl">Shelter-in-Place at Facility:</span> ${data.shelterInPlaceAtFacility||'___'}</div>
    <div class="field"><span class="fl">Shelter-in-Place Off-Site:</span> ${data.shelterInPlaceOffSite||'___'}</div>
    <div class="field"><span class="fl">Emergency Radio Location:</span> ${data.emergencyRadioLocation||'___'}</div>
    <h2>III. FIRE SAFETY</h2>
    <div class="field"><span class="fl">Fire Extinguisher Locations:</span></div>
    <ul>${(data.fireExtinguisherLocations||[]).filter(l=>l).map(l=>`<li>${l}</li>`).join('')||'<li>___</li>'}</ul>
    <div class="field"><span class="fl">Fire Assembly Area:</span> ${data.fireAssemblyArea||'___'}</div>
    <div class="field"><span class="fl">Fire Relocation Site (if cannot reenter):</span> ${data.fireRelocationSite||'___'}</div>
    <h2>IV. INCLEMENT WEATHER / SEVERE WEATHER</h2>
    <div class="field"><span class="fl">Severe Weather Shelter at Facility:</span> ${data.severeWeatherShelterFacility||'___'}</div>
    <div class="field"><span class="fl">Severe Weather Shelter Off-Site:</span> ${data.severeWeatherShelterOffSite||'___'}</div>
    <div class="field"><span class="fl">Tornado Safe Location:</span> ${data.tornadoSafePlace||'___'}</div>
    <h2>V. ILLNESS OR INJURY</h2>
    <div class="field"><span class="fl">First Aid Kit Locations:</span> ${data.firstAidKitLocations||'___'}</div>
    <h3>Qualified First Aid Personnel:</h3>
    <table><tr><th>Name</th><th>Location</th></tr>
    ${(data.firstAidPersonnel||[]).map(p=>`<tr><td>${p.name||'___'}</td><td>${p.location||'___'}</td></tr>`).join('')}</table>
    <div class="field" style="margin-top:8px"><span class="fl">Hospital:</span> ${data.hospitalName||'___'} | ${data.hospitalAddress||'___'} | ${data.hospitalPhone||'___'}</div>
    <h2>VI. UTILITIES AND MAINTENANCE EMERGENCIES</h2>
    <div class="field"><span class="fl">Flashlight Location:</span> ${data.flashlightLocation||'___'} &nbsp;|&nbsp; <span class="fl">Batteries:</span> ${data.batteriesLocation||'___'}</div>
    <div class="field"><span class="fl">Emergency Radio:</span> ${data.radioLocation||'___'} &nbsp;|&nbsp; <span class="fl">Cell Phone:</span> ${data.cellPhoneLocation||'___'}</div>
    <div class="field"><span class="fl">Emergency Water Supply:</span> ${data.waterSupplyLocation||'___'}</div>
    <div class="field"><span class="fl">Heat Closure Temp:</span> ${data.heatClosureTemp||'65'}°F or below &nbsp;|&nbsp; <span class="fl">Cooling Closure Temp:</span> ${data.coolingClosureTemp||'85'}°F or above</div>
    <table><tr><th>Service</th><th>Phone / Contact</th></tr>
    <tr><td>Water Company</td><td>${data.waterCompanyPhone||'___'}</td></tr>
    <tr><td>Power Company</td><td>${data.powerCompanyPhone||'___'}</td></tr>
    <tr><td>Public Works</td><td>${data.publicWorksPhone||'___'}</td></tr>
    <tr><td>HVAC Service</td><td>${data.hvacContactInfo||'___'}</td></tr>
    <tr><td>Plumbing</td><td>${data.plumbingContactInfo||'___'}</td></tr>
    <tr><td>Locksmith</td><td>${data.locksmithContactInfo||'___'}</td></tr>
    <tr><td>Telephone Repair</td><td>${data.telephoneRepairPhone||'___'}</td></tr>
    </table>
    <h2>VII. EMERGENCY CONTACTS</h2>
    <table><tr><th>Role / Service</th><th>Phone</th></tr>
    ${(data.emergencyContacts||[]).map(c=>`<tr><td>${c.role}</td><td>${c.phone||'___'}</td></tr>`).join('')}</table>
    <h2>VIII. INFANTS, TODDLERS & CHILDREN WITH SPECIAL NEEDS</h2>
    <div class="field"><span class="fl">Caregiver Go Bag Location:</span> ${data.caregiverGoBagLocation||'___'}</div>
    ${data.infantEvacuationNotes ? `<div class="field">${data.infantEvacuationNotes}</div>` : ''}
    <div style="margin-top:20px;border-top:1px solid #000;padding-top:10px;font-size:10px;font-style:italic;">
      This plan shall be reviewed annually. OCDEL/DECAL notified within 24 hours of any emergency. All staff trained on this plan annually.
    </div>
    </body></html>`);
    w.document.close(); w.print();
  };

  if (loading) return <Card><CardContent className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></CardContent></Card>;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Shield className="w-5 h-5 text-red-600" />
            Emergency Plan & Procedure
          </CardTitle>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => setShowHistory(!showHistory)}>
              <History className="w-4 h-4 mr-1" />History ({savedPlans.length})
            </Button>
            <Button variant="outline" size="sm" onClick={() => handlePrint()}>
              <Printer className="w-4 h-4 mr-1" />Print
            </Button>
            <Button size="sm" onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}Save
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">

        {/* History */}
        {showHistory && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold mb-3 text-gray-800">Saved Plans & Uploaded Documents</h4>
            {savedPlans.length === 0 ? <p className="text-sm text-gray-500">No records saved yet.</p> : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {savedPlans.map(plan => (
                  <div key={plan.id} className="flex items-center justify-between bg-white rounded-lg border border-gray-200 px-3 py-2">
                    <div className="flex-1 min-w-0">
                      {plan.recordType === 'file_upload' ? (
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-blue-600 flex-shrink-0" />
                          <div>
                            <span className="text-sm font-medium text-gray-800 truncate block">{plan.fileName}</span>
                            <span className="text-xs text-gray-400">Uploaded {plan.createdAt ? new Date(plan.createdAt).toLocaleDateString() : ''}</span>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <span className="text-sm font-medium text-gray-800">{plan.facilityName || 'Emergency Plan'}</span>
                          <span className="text-xs text-gray-400 ml-2">Saved {plan.createdAt ? new Date(plan.createdAt).toLocaleDateString() : ''}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1.5 ml-2 flex-shrink-0">
                      {plan.recordType === 'file_upload' ? (
                        <>
                          <Button size="sm" variant="outline" onClick={() => handleDownloadPlan(plan)} className="h-7 px-2 text-xs"><Download className="w-3 h-3 mr-1" />Download</Button>
                          <Button size="sm" variant="outline" onClick={() => handlePrintPlan(plan)} className="h-7 px-2 text-xs"><Printer className="w-3 h-3 mr-1" />Print</Button>
                        </>
                      ) : (
                        <>
                          <Button size="sm" variant="outline" onClick={() => { setFormData(prev => ({ ...prev, ...plan })); setShowHistory(false); }} className="h-7 px-2 text-xs">Load</Button>
                          <Button size="sm" variant="outline" onClick={() => handlePrintPlan(plan)} className="h-7 px-2 text-xs"><Printer className="w-3 h-3 mr-1" />Print</Button>
                        </>
                      )}
                      <Button size="sm" variant="outline" onClick={() => handleDeletePlan(plan.id)} className="h-7 w-7 p-0 text-red-500 border-red-200 hover:bg-red-50"><X className="w-3 h-3" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Upload */}
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
          <h4 className="font-medium mb-2 text-blue-800 text-sm">Upload Your Emergency Plan Document</h4>
          <div onDrop={handleDrop} onDragOver={e => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)}
            className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${dragOver ? 'border-blue-500 bg-blue-100' : 'border-blue-300'}`}
            onClick={() => !uploading && document.getElementById('epp-file-input').click()}>
            {uploading ? <div className="flex items-center justify-center gap-2"><Loader2 className="w-5 h-5 text-blue-500 animate-spin" /><span className="text-sm text-blue-600">Uploading...</span></div>
              : <><Upload className="w-6 h-6 mx-auto mb-1 text-gray-400" /><p className="text-sm text-gray-500">Click to upload or drag & drop your completed plan</p><p className="text-xs text-blue-400 mt-0.5">PDF, DOC, DOCX, JPG, PNG (Max 10MB)</p></>}
          </div>
          <input id="epp-file-input" type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={e => handleFileUpload(e.target.files?.[0])} className="hidden" />
        </div>

        {/* Preloaded Template Notice — informational only */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2" data-testid="preloaded-template-notice">
          <FileText className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800">
            A preloaded Emergency Plan & Procedure template is available below for your convenience. You may use the provided template or upload your own completed document.
          </p>
        </div>

        {/* Facility Information */}
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <h3 className="font-semibold text-sm mb-3">Facility Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {F("Facility Name *", "facilityName", "Center name")}
            {F("License Number", "licenseNumber", "License #")}
            {F("Facility Director", "facilityDirector", "Director name")}
            {F("Phone", "facilityPhone", "Main phone number")}
          </div>
          <div className="mt-3 space-y-1">
            <Label className="text-xs">Address</Label>
            <Textarea value={formData.facilityAddress} onChange={e => handleInputChange('facilityAddress', e.target.value)} placeholder="Facility address" rows={2} className="text-sm" />
          </div>
        </div>

        {/* I. Evacuation */}
        <div className="space-y-2">
          {S("I. Evacuation", "evacuation")}
          {expandedSections.evacuation && (
            <div className="p-4 border rounded-lg space-y-3">
              <p className="text-xs text-gray-500">Director or designated person contacts 911. Children's emergency contact numbers taken to evacuation area. Message posted on door/answering machine regarding relocation.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {F("Primary Assembly Area", "primaryAssemblyArea", "e.g. Adjacent playground / soccer field")}
                {F("Secondary Assembly Area (if primary blocked by smoke/hazards)", "secondaryAssemblyArea", "e.g. Fitness Center at [address]")}
                {F("Relocation Site (if facility cannot be re-entered)", "relocationSite", "e.g. Elementary School name")}
                {F("Relocation Site Address", "relocationSiteAddress", "Street address")}
              </div>
            </div>
          )}
        </div>

        {/* II. Lockdown / Shelter-in-Place */}
        <div className="space-y-2">
          {S("II. Lockdown / Shelter-in-Place", "lockdown")}
          {expandedSections.lockdown && (
            <div className="p-4 border rounded-lg space-y-3">
              <p className="text-xs text-gray-500">Lock doors and windows. Close curtains. Turn off lights. Keep everyone away from doors and windows. Notify parents of any lockdown. OCDEL/DECAL notified within 24 hours.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {F("Shelter-in-Place Location at Facility", "shelterInPlaceAtFacility", "e.g. Upstairs interior room")}
                {F("Shelter-in-Place Off-Site Location", "shelterInPlaceOffSite", "e.g. Fitness Center at [address]")}
                {F("Emergency Radio Location", "emergencyRadioLocation", "e.g. Employee room / Evacuation Pack")}
              </div>
            </div>
          )}
        </div>

        {/* III. Fire Safety */}
        <div className="space-y-2">
          {S("III. Fire Safety", "fire")}
          {expandedSections.fire && (
            <div className="p-4 border rounded-lg space-y-3">
              <p className="text-xs text-gray-500">Line children at nearest exit. Head count. Search rooms, close doors. Escort children in single file. Contact 911 after evacuation. OCDEL/DECAL notified within 24 hours.</p>
              <div>
                <Label className="text-xs mb-2 block">Fire Extinguisher Locations:</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {formData.fireExtinguisherLocations.map((loc, idx) => (
                    <Input key={idx} value={loc} onChange={e => handleArrayChange('fireExtinguisherLocations', idx, e.target.value)} placeholder={`Location ${idx + 1}`} className="h-8 text-sm" />
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {F("Fire Assembly Area", "fireAssemblyArea", "Where children go during fire evacuation")}
                {F("Fire Relocation Site (if cannot re-enter)", "fireRelocationSite", "Alternate site name and address")}
              </div>
            </div>
          )}
        </div>

        {/* IV. Carbon Monoxide */}
        <div className="space-y-2">
          {S("IV. Carbon Monoxide", "carbon")}
          {expandedSections.carbon && (
            <div className="p-4 border rounded-lg space-y-3">
              <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-800">
                <strong>Symptoms:</strong> Sudden flu-like illness, dizziness, headaches, nausea, fluttering heartbeat, cherry-red lips, unconsciousness.<br/>
                <strong>If suspected:</strong> Get victim into fresh air. Call 911. Get everyone out. Open windows.
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">CO Detector Locations</Label>
                  <Textarea value={formData.coDetectorLocations || ""} onChange={e => handleInputChange('coDetectorLocations', e.target.value)} placeholder="List detector locations" rows={2} className="text-sm" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">HVAC / Fuel-Burning Equipment Service Contact</Label>
                  <Input value={formData.hvacContactInfo || ""} onChange={e => handleInputChange('hvacContactInfo', e.target.value)} placeholder="Contact info for annual inspection" className="h-8 text-sm" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* V. Inclement Weather */}
        <div className="space-y-2">
          {S("V. Inclement Weather / Severe Weather", "weather")}
          {expandedSections.weather && (
            <div className="p-4 border rounded-lg space-y-3">
              <p className="text-xs text-gray-500">During severe weather watch, staff gather children at shelter-in-place location. No children leave during severe weather without legal parent/guardian. During tornado warning: children sit with backs to wall, heads between knees.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {F("Severe Weather Shelter at Facility", "severeWeatherShelterFacility", "e.g. Interior room, lowest floor")}
                {F("Severe Weather Shelter Off-Site", "severeWeatherShelterOffSite", "e.g. Fitness Center at [address]")}
                {F("Tornado Safe Place", "tornadoSafePlace", "e.g. Interior hallway, away from windows")}
              </div>
            </div>
          )}
        </div>

        {/* VI. Illness or Injury */}
        <div className="space-y-2">
          {S("VI. Illness or Injury — First Aid", "illness")}
          {expandedSections.illness && (
            <div className="p-4 border rounded-lg space-y-3">
              <p className="text-xs text-gray-500">Remove other children from area. Do not move injured child. Provide first aid until emergency personnel arrive. Call 911 if necessary. Contact parents. OCDEL/DECAL notified within 24 hours.</p>
              {F("First Aid Kit Locations", "firstAidKitLocations", "e.g. In every room in center")}
              <div>
                <Label className="text-xs mb-2 block">Qualified First Aid Personnel:</Label>
                {formData.firstAidPersonnel.map((person, idx) => (
                  <div key={idx} className="flex gap-2 mb-2">
                    <Input value={person.name} onChange={e => handleObjectArrayChange('firstAidPersonnel', idx, 'name', e.target.value)} placeholder="Name" className="h-8 text-sm flex-1" />
                    <Input value={person.location} onChange={e => handleObjectArrayChange('firstAidPersonnel', idx, 'location', e.target.value)} placeholder="Location / Room" className="h-8 text-sm flex-1" />
                    {formData.firstAidPersonnel.length > 1 && (
                      <Button variant="ghost" size="sm" onClick={() => removeFromArray('firstAidPersonnel', idx)} className="h-8 w-8 p-0 text-red-400"><Trash2 className="w-3 h-3" /></Button>
                    )}
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => addToArray('firstAidPersonnel', { name: "", location: "" })} className="text-xs h-7">
                  <Plus className="w-3 h-3 mr-1" />Add Person
                </Button>
              </div>
              <div className="border-t pt-3">
                <Label className="text-xs font-semibold mb-2 block">Hospital (for serious injury/death of child):</Label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {F("Hospital Name", "hospitalName", "Hospital name")}
                  {F("Hospital Address", "hospitalAddress", "Street address")}
                  {F("Hospital Phone", "hospitalPhone", "Phone number")}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* VII. Communicable Disease */}
        <div className="space-y-2">
          {S("VII. Communicable Disease", "communicable")}
          {expandedSections.communicable && (
            <div className="p-4 border rounded-lg space-y-3">
              <p className="text-xs text-gray-500">Child with 101°F+ oral temperature and contagious symptom not accepted or allowed to remain. Parents notified. Health department notified on communicable diseases per regulatory chart.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Parent Notification Method</Label>
                  <Input value={formData.communicableNotifyMethod || ""} onChange={e => handleInputChange('communicableNotifyMethod', e.target.value)} placeholder="e.g. Memo / Letter to Parents" className="h-8 text-sm" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Health Department Contact</Label>
                  <Input value={formData.healthDeptContact || ""} onChange={e => handleInputChange('healthDeptContact', e.target.value)} placeholder="Phone number" className="h-8 text-sm" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* VIII. Utilities */}
        <div className="space-y-2">
          {S("VIII. Utilities and Maintenance Emergencies", "utilities")}
          {expandedSections.utilities && (
            <div className="p-4 border rounded-lg space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {F("Flashlight Location", "flashlightLocation", "Location")}
                {F("Spare Batteries Location", "batteriesLocation", "Location")}
                {F("Emergency Radio Location", "radioLocation", "Location")}
                {F("Emergency Water Supply Location", "waterSupplyLocation", "Location")}
                {F("Cellular Phone Location", "cellPhoneLocation", "Location")}
              </div>
              <div className="border-t pt-3">
                <p className="text-xs font-semibold text-gray-700 mb-2">Temperature Closure Thresholds:</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Close if temperature drops to or below (°F)</Label>
                    <Input value={formData.heatClosureTemp || "65"} onChange={e => handleInputChange('heatClosureTemp', e.target.value)} placeholder="e.g. 65" className="h-8 text-sm" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Close if temperature rises to or above (°F)</Label>
                    <Input value={formData.coolingClosureTemp || "85"} onChange={e => handleInputChange('coolingClosureTemp', e.target.value)} placeholder="e.g. 85" className="h-8 text-sm" />
                  </div>
                </div>
              </div>
              <div className="border-t pt-3">
                <p className="text-xs font-semibold text-gray-700 mb-2">Service Contact Numbers:</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {F("Water Company", "waterCompanyPhone", "Phone number")}
                  {F("Power Company", "powerCompanyPhone", "Phone number")}
                  {F("Public Works", "publicWorksPhone", "Phone number")}
                  {F("HVAC Service", "hvacContactInfo", "Contact info")}
                  {F("Plumbing", "plumbingContactInfo", "Contact info")}
                  {F("Locksmith", "locksmithContactInfo", "Contact info")}
                  {F("Telephone Repair", "telephoneRepairPhone", "Phone number")}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* IX. Loss of a Child / Serious Injury */}
        <div className="space-y-2">
          {S("IX. Loss of a Child / Serious Injury / Death", "lossOfChild")}
          {expandedSections.lossOfChild && (
            <div className="p-4 border rounded-lg space-y-3">
              <div className="bg-red-50 border border-red-200 rounded p-3 text-xs text-red-800">
                <strong>Loss of a Child:</strong> Teacher notifies Director immediately. Head count. Note child's clothing description. Check playground, vehicles, bathrooms, closets. If not found: call 911. Notify parents. OCDEL/DECAL within 24 hours.<br/><br/>
                <strong>Serious Injury / Death:</strong> Remove other children. Do not move child. Provide first aid. Call 911. Contact parents — child transported to hospital below. OCDEL/DECAL within 24 hours.
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {F("Hospital Name", "hospitalName", "Hospital name")}
                {F("Hospital Address", "hospitalAddress", "Street address")}
                {F("Hospital Phone", "hospitalPhone", "Phone number")}
              </div>
            </div>
          )}
        </div>

        {/* X. Bomb Threat */}
        <div className="space-y-2">
          {S("X. Bomb Threat Procedure", "bombThreat")}
          {expandedSections.bombThreat && (
            <div className="p-4 border rounded-lg space-y-3">
              <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-800">
                <strong>Steps:</strong> Check caller ID. Signal staff to call 911. Before hanging up — note: where is bomb, when will it explode, what does it look like, why was it placed. Note caller's voice and background noise. Do NOT touch suspicious packages. Do NOT use cell phones or 2-way radios. Follow evacuation procedure if directed by police.
              </div>
              {F("Evacuation Site if Bomb Threat Requires Evacuation", "bombThreatEvacSite", "Same as primary relocation site")}
            </div>
          )}
        </div>

        {/* XI. Chemical / Radiation / Dangerous Person */}
        <div className="space-y-2">
          {S("XI. Chemical, Radiation & Dangerous Person", "chemical")}
          {expandedSections.chemical && (
            <div className="p-4 border rounded-lg space-y-3">
              <div className="bg-gray-50 border rounded p-3 text-xs text-gray-700 space-y-2">
                <div><strong>Chemical/Radiation Exposure:</strong> Monitor local radio for instructions. Prepare to shelter-in-place or evacuate per authorities. If exposed outside: remove outer clothing, take shelter, wash with cool water and soap, flush eyes with water.</div>
                <div><strong>Dangerous Person:</strong> Alert all staff. Initiate lockdown. Call 911 from safe location. Isolate person from children — do not physically restrain. If children are outside and person is outside: gather children and return indoors or evacuate to designated site.</div>
              </div>
            </div>
          )}
        </div>

        {/* XII. Infants, Toddlers & Special Needs */}
        <div className="space-y-2">
          {S("XII. Infants, Toddlers & Children with Special Needs", "specialNeeds")}
          {expandedSections.specialNeeds && (
            <div className="p-4 border rounded-lg space-y-3">
              <p className="text-xs text-gray-500">Evacuate by classroom grouping, maintaining ratio. Use nearest exits. Cribs with wheels used for infant evacuation. Children with disabilities/mobility limitations remain on ground floor for evacuation. Special medical equipment (oxygen, EpiPens, etc.) brought along.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {F("Caregiver Go Bag Location (diapers, wipes, water, etc.)", "caregiverGoBagLocation", "e.g. Under sink in infant/toddler room")}
                <div className="space-y-1">
                  <Label className="text-xs">Additional Notes for Infants / Special Needs Evacuation</Label>
                  <Textarea value={formData.infantEvacuationNotes || ""} onChange={e => handleInputChange('infantEvacuationNotes', e.target.value)} placeholder="Any additional evacuation notes..." rows={2} className="text-sm" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Emergency Contacts */}
        <div className="space-y-2">
          {S("Emergency Contacts", "contacts")}
          {expandedSections.contacts && (
            <div className="p-4 border rounded-lg space-y-2">
              {formData.emergencyContacts.map((contact, idx) => (
                <div key={idx} className="flex gap-2">
                  <Input value={contact.role} onChange={e => handleObjectArrayChange('emergencyContacts', idx, 'role', e.target.value)} placeholder="Role / Service" className="h-8 text-sm flex-1" />
                  <Input value={contact.phone} onChange={e => handleObjectArrayChange('emergencyContacts', idx, 'phone', e.target.value)} placeholder="Phone number" className="h-8 text-sm flex-1" />
                  {formData.emergencyContacts.length > 1 && (
                    <Button variant="ghost" size="sm" onClick={() => removeFromArray('emergencyContacts', idx)} className="h-8 w-8 p-0 text-red-400"><Trash2 className="w-3 h-3" /></Button>
                  )}
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => addToArray('emergencyContacts', { role: "", phone: "" })} className="text-xs h-7 mt-1">
                <Plus className="w-3 h-3 mr-1" />Add Contact
              </Button>
            </div>
          )}
        </div>

        {/* Approval */}
        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <h3 className="font-semibold text-sm mb-3">Plan Approval & Review</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {F("Approved By", "approvedBy", "Director / Supervisor name")}
            {F("Approval Date", "approvalDate", "", "date")}
            {F("Last Review Date", "lastReviewDate", "", "date")}
          </div>
          <p className="text-xs text-gray-500 mt-3 italic">
            This plan shall be reviewed annually and updated as needed. OCDEL/DECAL notified within 24 hours of any emergency event. All staff trained on this plan annually — maintain signed training log.
          </p>
        </div>

      </CardContent>
    </Card>
  );
};

export default EmergencyPlanProcedureTab;
